import { Capacitor } from '@capacitor/core'
import { Homecheck } from 'capacitor-homecheck'

export type ClonePhotoRequest = {
  fileName: string
  oldPath: string
  dir: string
}

export type SavePhotoRequest = {
  fileName: string
  base64: string
  dir: string
  permissions?: 'public' | 'private'
}

const nativeFilePlugin = Homecheck

// 첫 실행 여부 확인
export const checkFirstLaunch = async (): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) return false
  try {
    const { value } = await nativeFilePlugin.checkFirstLaunch()
    return value
  } catch (error) {
    console.error('checkFirstLaunch 실패:', error)
    return false
  }
}

// iOS에서 Photos Library 권한 요청
export const requestPhotosPermission = async (): Promise<{
  status: number
  authorized: boolean
}> => {
  if (!Capacitor.isNativePlatform()) return { status: -1, authorized: false }
  if (Capacitor.getPlatform() === 'android') return { status: -1, authorized: false }
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = await (nativeFilePlugin as any).requestPhotosPermission()
    return result
  } catch (error) {
    console.error('requestPhotosPermission 실패:', error)
    return { status: -1, authorized: false }
  }
}

// 촬영한 사진을 갤러리 + 앱 저장소에 복사
export const saveCapturedPhoto = async (request: ClonePhotoRequest): Promise<string> => {
  if (!Capacitor.isNativePlatform()) {
    throw new Error('saveCapturedPhoto는 네이티브 플랫폼에서만 지원됩니다.')
  }

  const { absolutePath } = await nativeFilePlugin.saveCapturedPhoto({
    ...request,
  })

  return absolutePath
}

// Base64 이미지를 앱 저장소 또는 갤러리에 저장
export const savePhoto = async (request: SavePhotoRequest): Promise<string> => {
  if (!Capacitor.isNativePlatform()) {
    throw new Error('savePhoto는 네이티브 플랫폼에서만 지원됩니다.')
  }

  const { absolutePath } = await nativeFilePlugin.savePhoto({
    ...request,
  })
  return absolutePath
}

// 앱 저장소 경로를 가져오기
export const getAppStoragePath = async (): Promise<string> => {
  if (!Capacitor.isNativePlatform()) {
    throw new Error('getAppStoragePath는 네이티브 플랫폼에서만 지원됩니다.')
  }

  const { absolutePath } = await nativeFilePlugin.getAppStoragePath()
  return absolutePath
}

// 특정 폴더에서 패턴에 맞는 파일들을 삭제
export const deleteFilesInFolder = async (request: {
  folderName: string
  pattern: string
}): Promise<number> => {
  if (!Capacitor.isNativePlatform()) {
    throw new Error('deleteFilesInFolder는 네이티브 플랫폼에서만 지원됩니다.')
  }

  const { deletedCount } = await nativeFilePlugin.deleteFilesInFolder(request)
  return deletedCount
}

/**
 * 특정 파일 삭제
 * @param request.filePath - 삭제할 파일 경로 (플랫폼별 형식 자동 처리)
 */
export const deleteFile = async (request: { filePath: string }): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    throw new Error('deleteFile는 네이티브 플랫폼에서만 지원됩니다.')
  }

  // 플랫폼별 파일 경로 처리
  let processedPath = request.filePath
  if (!processedPath.startsWith('local-file://')) {
    // 플랫폼에 관계없이 local-file:// 프리픽스 추가
    if (processedPath.startsWith('/')) {
      processedPath = `local-file://${processedPath}`
    }
  }

  const { success } = await nativeFilePlugin.deleteFile({ filePath: processedPath })
  return success
}

// 파일을 WebP 형식으로 변환
export const convertFileToWebp = async (request: {
  resourcePath: string
}): Promise<{ resourcePath: string; value: string }> => {
  if (!Capacitor.isNativePlatform()) {
    throw new Error('convertFileToWebp는 네이티브 플랫폼에서만 지원됩니다.')
  }

  const result = await nativeFilePlugin.convertFileToWebp(request)
  return result
}
